import { useMemo, useState } from 'react'

import { Skeleton } from 'antd'
import { Flex, DateRangePicker } from 'govwell-ui'
import {
  DateRangeType,
  DateRangePickerValue,
} from 'govwell-ui/components/DateRangePicker/DateRangePicker'
import { getDateRangePickerValue } from 'govwell-ui/components/DateRangePicker/util'
import { DateRange } from 'types/graphql'

import AIPageContainer from 'src/components/AIAssistant/AIPageContainer'
import BarChart from 'src/components/Visualizations/BarChart/BarChart'
import Tile, { TileType } from 'src/components/Visualizations/Tile'
import {
  extractViewDataElementNumber,
  getBucketStartLabel,
} from 'src/components/Visualizations/util'
import { useAIROIQuery } from 'src/fetch/aiChat'
import { useGetDataForWidgetQuery } from 'src/fetch/widgets'
import { useMyOrg } from 'src/layouts/AppStateContextLayout/utils'
import { filterNil } from 'src/utils'
import { DateFormats } from 'src/utils/date'

const dateKey = 'Date'
const substantiveAnswerYesKey = 'Answered automatically'
const substantiveAnswerNoKey = 'Not answered automatically'
const keys = [substantiveAnswerNoKey, substantiveAnswerYesKey] // bottom-to-top

const AIAssistantAnalyzePage = () => {
  const org = useMyOrg()
  const [dateRange, setDateRange] = useState<DateRangePickerValue>(
    getDateRangePickerValue(DateRangeType.MonthToDate)
  )
  const selectedDateRange: DateRange = useMemo(() => {
    return {
      start: dateRange.startDate?.toISOString() ?? '',
      end: dateRange.endDate?.toISOString() ?? '',
    }
  }, [dateRange])

  const { aiChatROIData, isLoading } = useAIROIQuery({
    startDate: selectedDateRange.start,
    endDate: selectedDateRange.end,
  })

  const {
    buckets: timeDataBuckets,
    bucketSize: timeBucketSize,
    isLoading: isTimeSeriesLoading,
  } = useGetDataForWidgetQuery({
    functionInputColumnTemplate: null,
    functions: ['Count'],
    dateRange: selectedDateRange,
    dateRangeColumnTemplate: { columnTemplateGeneratedId: 'AssistantAIQuestionCreatedAt' },
    filters: [],
    groupByColumnTemplates: [
      { columnTemplateGeneratedId: 'CommunityAssistantAIResponseWasSubstantive' },
    ],
    groupSortBy: 'Key',
    groupSortMode: 'Asc',
    viewType: 'CommunityAssistantAIChats',
    isTimeSeries: true,
  })

  const timeSeriesData = useMemo(() => {
    if (!timeDataBuckets || !timeBucketSize) {
      return []
    }

    return filterNil(
      timeDataBuckets.map((bucket) => {
        if (bucket.groups?.length === 0) {
          return null
        }

        const row: Record<string, number | string> = {
          [dateKey]: getBucketStartLabel(timeBucketSize, bucket.start, org?.timeZone),
        }

        bucket.groups?.forEach((group) => {
          const value = extractViewDataElementNumber(group.values?.[0]?.value) ?? 0
          const key = group.groupBys?.[0]?.key
          if (key?.__typename === 'ViewDataElementBoolean') {
            const name = key?.bool ? substantiveAnswerYesKey : substantiveAnswerNoKey
            row[name] = value
          }
        })

        return row
      })
    )
  }, [timeDataBuckets, timeBucketSize, org?.timeZone])

  const { numberOfAIChats, percentOfAIChatsWithSubstantiveAnswer, percentOfAIChatsWithThumbsUp } =
    useMemo(() => {
      return {
        numberOfAIChats: aiChatROIData?.numberOfAIChats ?? 0,
        percentOfAIChatsWithSubstantiveAnswer:
          aiChatROIData?.percentOfAIChatsWithSubstantiveAnswer ?? 0,
        percentOfAIChatsWithThumbsUp: aiChatROIData?.percentOfAIChatsWithThumbsUp ?? 0,
      }
    }, [aiChatROIData])

  return (
    <AIPageContainer
      title="Analyze Community Assistant"
      subtitle="Performance, usage metrics, and conversation insights"
    >
      <Flex direction="column" gap={4}>
        <DateRangePicker
          label="Date Range"
          dateFormat={DateFormats.MonthNameDateNoTime}
          value={dateRange}
          onChange={setDateRange}
          timeZone={org?.timeZone}
          width="100%"
        />
        {isLoading && <Skeleton active />}
        {!isLoading && (
          <Flex gap={2}>
            <Tile
              type={TileType.Number}
              value={numberOfAIChats}
              label="Questions received"
              tooltip="The number of questions received by the Community Assistant."
            />
            {!!percentOfAIChatsWithSubstantiveAnswer && (
              <Tile
                type={TileType.Percent}
                value={percentOfAIChatsWithSubstantiveAnswer}
                label="Questions answered automatically"
                tooltip="The percent of questions that the Community Assistant answered automatically."
              />
            )}
            {!!percentOfAIChatsWithThumbsUp && (
              <Tile
                type={TileType.Percent}
                value={percentOfAIChatsWithThumbsUp}
                label="Answers given 'thumbs up' by the public"
                tooltip="The percent of the Community Assistant's answers that received a thumbs-up reaction."
              />
            )}
          </Flex>
        )}
        <Flex gap={2}>
          <BarChart
            data={timeSeriesData}
            indexBy={dateKey}
            keys={keys}
            bottomAxisLabel="Date"
            leftAxisLabel="Questions"
            label="Questions Asked by Date"
            labelTooltip="Total questions, for each date bucket"
            isLoading={isTimeSeriesLoading}
            isTimeSeries
          />
        </Flex>
      </Flex>
    </AIPageContainer>
  )
}

export default AIAssistantAnalyzePage
